"use client";

import { CasinoGameTag } from "@finbackoffice/enums";
import {
    CasinoGameTypeEnum,
    CasinoWidgetFolderEnum,
    CasinoSectionsStyleEnum,
    CasinoWidgetImgSrcEnum,
    ProvidersEnum,
} from "@finbackoffice/fe-core";
import {
    ICasinoDataState,
    PaginationType,
    UseCasinoGamesHookOptions,
} from "@finbackoffice/site-core";
import {
    createContext,
    Dispatch,
    FC,
    SetStateAction,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import dynamic from "next/dynamic";
import { INotificationState, useCasinoGames } from "hooks";
import PreloadComponent from "components/base/preload-component/PreloadComponent";
import { ModalsContextV2 } from "contexts";
import { ModalTypes } from "utils/constants";

const CasinoGamesMosaic = dynamic(() => import("./sections-style/mosaic/CasinoGamesMosaic"), {
    loading: () => <PreloadComponent style={{ height: 356 }} />,
    ssr: false,
});

const CasinoGamesRow = dynamic(() => import("./sections-style/row/CasinoGamesRow"), {
    loading: () => <PreloadComponent style={{ height: 265 }} />,
    ssr: false,
});

const CasinoGamesShop = dynamic(() => import("./sections-style/shop/CasinoGamesShop"), {
    loading: () => <PreloadComponent style={{ height: 381 }} />,
    ssr: false,
});

type IProps = {
    id: string;
    style?: CasinoSectionsStyleEnum;
    tag?: CasinoGameTag;
    gameTypes: CasinoGameTypeEnum[] | CasinoGameTypeEnum;
    options?: UseCasinoGamesHookOptions;
    provider?: ProvidersEnum | "All";
    notification?: INotificationState;
    hideTag?: CasinoGameTag[];
    href?: string;
    wrapperClassName?: string;
    backImg?: boolean;
    imgSrc?: CasinoWidgetImgSrcEnum;
    imgFolder: CasinoWidgetFolderEnum;
};

export type ICasinoGamesContext = {
    id: string;
    casinoGamesState: ICasinoDataState;
    setPagination: Dispatch<SetStateAction<PaginationType>>;
    hasMore: boolean;
    notification: INotificationState;
    hideTag?: CasinoGameTag[];
    href?: string;
    options: UseCasinoGamesHookOptions | undefined;
    tag?: CasinoGameTag;
    imgSrc?: CasinoWidgetImgSrcEnum;
    imgFolder: CasinoWidgetFolderEnum;
    gameTypes: CasinoGameTypeEnum[];
    expanded: boolean;
    setExpanded: Dispatch<SetStateAction<boolean>>;
};

export const CasinoGamesContext = createContext<ICasinoGamesContext>(null as any);

const CasinoGames: FC<IProps> = ({
    id,
    style,
    tag,
    gameTypes,
    options,
    hideTag,
    provider = "All",
    href,
    backImg,
    imgSrc,
    imgFolder,
}) => {
    const gameTypesMemo = useMemo(
        () => (typeof gameTypes === "string" ? [gameTypes] : gameTypes),
        [gameTypes],
    );
    const { casinoGamesState, setPagination, hasMore, notification } = useCasinoGames(
        gameTypesMemo,
        tag ?? "",
        provider,
        options,
    );
    const { currentModal } = useContext(ModalsContextV2);

    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        if (currentModal === ModalTypes.LOGIN && expanded) {
            setExpanded(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentModal]);

    const renderContent = useMemo(() => {
        switch (style) {
            case CasinoSectionsStyleEnum.Row:
                return <CasinoGamesRow sectionStyle={CasinoSectionsStyleEnum.Row} />;
            case CasinoSectionsStyleEnum.Narrow:
                return (
                    <CasinoGamesRow
                        sectionStyle={CasinoSectionsStyleEnum.Narrow}
                        gameMediaFolder="narrow"
                    />
                );
            case CasinoSectionsStyleEnum.RoundRow:
                return (
                    <CasinoGamesRow
                        sectionStyle={CasinoSectionsStyleEnum.RoundRow}
                        layout="rounded"
                        backImg={backImg}
                    />
                );
            case CasinoSectionsStyleEnum.MiniRow:
                return (
                    <CasinoGamesRow
                        sectionStyle={CasinoSectionsStyleEnum.MiniRow}
                        layout="squared"
                    />
                );
            case CasinoSectionsStyleEnum.WideRow:
                return (
                    <CasinoGamesRow
                        sectionStyle={CasinoSectionsStyleEnum.WideRow}
                        gameMediaFolder={options?.megaways && !expanded ? "megaways" : imgFolder}
                    />
                );
            case CasinoSectionsStyleEnum.NarrowRow:
                return (
                    <CasinoGamesRow
                        sectionStyle={CasinoSectionsStyleEnum.NarrowRow}
                        gameMediaFolder="narrow"
                    />
                );
            case CasinoSectionsStyleEnum.Mosaic:
            case CasinoSectionsStyleEnum.MosaicMix:
            case CasinoSectionsStyleEnum.MosaicRain:
                return <CasinoGamesMosaic layout={style} />;
            case CasinoSectionsStyleEnum.Shop:
                return <CasinoGamesShop />;
            default:
                return <CasinoGamesRow />;
        }
    }, [style, backImg, options?.megaways, expanded, imgFolder]);

    const value = useMemo(
        () => ({
            id,
            casinoGamesState,
            hasMore,
            notification,
            setPagination,
            hideTag,
            href,
            options,
            imgSrc,
            imgFolder,
            gameTypes: gameTypesMemo,
            expanded,
            setExpanded,
        }),
        [
            id,
            casinoGamesState,
            hasMore,
            notification,
            setPagination,
            hideTag,
            href,
            options,
            imgSrc,
            imgFolder,
            gameTypesMemo,
            expanded,
        ],
    );

    return <CasinoGamesContext.Provider value={value}>{renderContent}</CasinoGamesContext.Provider>;
};

export default CasinoGames;
